/**
 * React Query defaults (§4.4): reads are cached briefly and refetched on focus; nothing that represents an
 * outcome, a payment, stock or a claim right is ever shown optimistically — those queries use
 * `OUTCOME_QUERY_OPTIONS` (no cache, no retry on 4xx) and mutations carry an Idempotency-Key the caller keeps
 * across retries (docs/modules/web.md "Data loading rules").
 */
import { QueryClient } from '@tanstack/react-query';
import { ApiError } from './api/types';

const retryReads = (failureCount: number, error: unknown): boolean => {
  if (error instanceof ApiError && error.status < 500 && error.status !== 429) return false;
  return failureCount < 2;
};

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        retry: retryReads,
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        throwOnError: false,
      },
      mutations: {
        // Never auto-retry a mutation: the screen retries with the SAME Idempotency-Key after a status check.
        retry: 0,
      },
    },
  });
}

/** For outcome / payment / claim / stock reads: always fresh, never served from cache, never optimistic. */
export const OUTCOME_QUERY_OPTIONS = {
  staleTime: 0,
  gcTime: 0,
  retry: retryReads,
  refetchOnWindowFocus: true,
} as const;
